/**
 * Quiz glossary — bilingual tooltip definitions for terms used inside
 * quiz question descriptions and per-option help text.
 *
 * Distinct from the wiki glossary (`web/src/features/wiki/glossary.ts`):
 *   - This one is for quiz-flow microcopy (short tooltips, 1 sentence).
 *   - The wiki glossary is for article prose (deeper definitions).
 *   - The slug spaces do not overlap; an entry in one does not implicate
 *     the other.
 *
 * Usage:
 *   Embed a marker in an i18n string: `... {{term:blue_card}} ...`.
 *   The Renderer (see `web/src/features/quiz/components/GlossText.tsx`)
 *   splits the string on the marker and wraps the term's surface label
 *   in an inline tooltip trigger.
 *
 * Adding a term:
 *   1. Append a new entry below. `slug` is kebab-case ASCII.
 *   2. Both `en` and `ru` are mandatory before merge — never auto-translate.
 *   3. Keep `short` to one sentence (≤ 160 chars). Deeper context belongs
 *      in the wiki article the term references.
 *   4. Optionally provide `wiki_href` to deep-link the tooltip to the
 *      relevant wiki topic — surface as a "Read more →" link in the popover.
 *
 * Per CV.2 §7.
 */

import type { LangCode } from '@/features/wiki/architecture';

export interface QuizGlossaryEntry {
  /** Surface label and 1-sentence definition per language. */
  en: { label: string; short: string };
  ru: { label: string; short: string };
  /** Optional deep-link to the wiki topic that covers this concept. */
  wiki_href?: string;
}

export const QUIZ_GLOSSARY: Record<string, QuizGlossaryEntry> = {
  // ─── Visa programmes ──────────────────────────────────────────────
  'blue-card': {
    en: {
      label: 'Blue Card',
      short:
        'EU-wide skilled-worker residency for non-EU IT professionals — typically requires a job offer above ~€45k.',
    },
    ru: {
      label: 'Blue Card',
      short:
        'Общеевропейская виза для квалифицированных не-граждан ЕС в IT — обычно требуется оффер выше ~€45k.',
    },
  },
  'critical-skills': {
    en: {
      label: 'Critical Skills',
      short: 'Ireland\'s fast-track work permit for high-demand IT roles.',
    },
    ru: {
      label: 'Critical Skills',
      short: 'Ирландская ускоренная рабочая виза для дефицитных IT-ролей.',
    },
  },
  'skilled-worker': {
    en: {
      label: 'Skilled Worker visa',
      short: 'UK\'s main work-visa for sponsored tech roles.',
    },
    ru: {
      label: 'Skilled Worker',
      short: 'Основная рабочая виза Великобритании под спонсорские IT-роли.',
    },
  },
  'express-entry': {
    en: {
      label: 'Express Entry',
      short:
        'Canada\'s points-based skilled-migrant program — common path for IT relocants.',
    },
    ru: {
      label: 'Express Entry',
      short:
        'Канадская балльная система для квалифицированных мигрантов — типичный путь IT-релоканта.',
    },
  },
  'golden-visa': {
    en: {
      label: 'golden visa',
      short:
        'An investor or property-purchase residency program; not employment-linked.',
    },
    ru: {
      label: 'инвесторская виза',
      short:
        'ВНЖ за инвестиции или покупку недвижимости; не связана с наймом.',
    },
  },

  // ─── Employment & hiring vocabulary ───────────────────────────────
  sponsorship: {
    en: {
      label: 'sponsorship',
      short: 'When an employer initiates and pays for your work-visa application.',
    },
    ru: {
      label: 'спонсорство',
      short:
        'Когда работодатель инициирует и оплачивает вашу заявку на рабочую визу.',
    },
  },
  'labour-market': {
    en: {
      label: 'labour market',
      short:
        'The pool of available jobs, employers, and salaries in a country at a given time.',
    },
    ru: {
      label: 'рынок труда',
      short:
        'Совокупность доступных вакансий, работодателей и зарплат в стране в данный момент.',
    },
  },
  'technical-interview': {
    en: {
      label: 'technical interview',
      short:
        'An interview that tests programming skills — usually live coding, system design, or take-home tasks.',
    },
    ru: {
      label: 'техническое интервью',
      short:
        'Интервью на проверку программистских навыков — обычно live coding, system design или такие задачи.',
    },
  },
  offer: {
    en: {
      label: 'offer',
      short:
        'A written job offer with salary, equity, start date, and conditions.',
    },
    ru: {
      label: 'оффер',
      short:
        'Письменное предложение работы с зарплатой, опционами, датой выхода и условиями.',
    },
  },
  seniority: {
    en: {
      label: 'seniority',
      short:
        'IT career ladder: Junior 0–2 yrs, Mid 2–5, Senior 5–10, Staff+/Principal 10+.',
    },
    ru: {
      label: 'сеньорность',
      short:
        'IT-лестница: Junior 0–2 года, Mid 2–5, Senior 5–10, Staff+/Principal 10+.',
    },
  },
};

export type QuizGlossarySlug = keyof typeof QUIZ_GLOSSARY;

export function getQuizGlossaryEntry(slug: string): QuizGlossaryEntry | null {
  return QUIZ_GLOSSARY[slug] ?? null;
}

/**
 * One segment in a parsed glossary-aware string. Either a plain text run,
 * or a term marker that the renderer wraps in a tooltip.
 */
export type GlossSegment =
  | { kind: 'text'; text: string }
  | { kind: 'term'; slug: string; entry: QuizGlossaryEntry; lang: GlossLang };

/** Languages the glossary carries surface labels + definitions for. */
type GlossLang = keyof Pick<QuizGlossaryEntry, 'en' | 'ru'>;

function isGlossLang(v: LangCode): v is GlossLang {
  return v === 'en' || v === 'ru';
}

/**
 * Parse `{{term:slug}}` markers out of a translated string and split it
 * into a sequence of text + term segments. Unknown slugs fall back to
 * plain text so a typo in i18n does not throw at render time.
 */
export function parseGlossaryString(input: string, lang: LangCode): GlossSegment[] {
  const glossLang: GlossLang = isGlossLang(lang) ? lang : 'en';
  const segments: GlossSegment[] = [];
  const re = /\{\{term:([a-z0-9-]+)\}\}/g;
  let lastIndex = 0;
  for (const match of input.matchAll(re)) {
    const idx = match.index ?? 0;
    if (idx > lastIndex) {
      segments.push({ kind: 'text', text: input.slice(lastIndex, idx) });
    }
    const slug = match[1];
    const entry = QUIZ_GLOSSARY[slug];
    if (entry) {
      segments.push({ kind: 'term', slug, entry, lang: glossLang });
    } else {
      segments.push({ kind: 'text', text: match[0] });
    }
    lastIndex = idx + match[0].length;
  }
  if (lastIndex < input.length) {
    segments.push({ kind: 'text', text: input.slice(lastIndex) });
  }
  return segments;
}
